import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Menu,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  TrendingUp as TrendingIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Notification type
// ---------------------------------------------------------------------------

interface NotificationItem {
  id: number;
  title: string;
  description: string;
  time: string;
  type: string;
  read: boolean;
}

// ---------------------------------------------------------------------------
// TrendingCandidate type
// ---------------------------------------------------------------------------

interface TrendingCandidate {
  id: number;
  name: string;
  party: string;
  trend: 'up' | 'down' | 'flat';
  mentions: string;
  change: string;
  avatar: string;
  profileImage?: string;
  color: string;
}

// ---------------------------------------------------------------------------
// NotificationsMenu
// ---------------------------------------------------------------------------

interface NotificationsMenuProps {
  anchorEl: HTMLElement | null;
  notifications: NotificationItem[];
  onClose: () => void;
}

export const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  anchorEl,
  notifications,
  onClose,
}) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
    PaperProps={{ sx: { mt: 2, width: 360, maxHeight: 480, overflow: 'auto' } }}
    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
  >
    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Latest Updates
      </Typography>
    </Box>
    {notifications.length === 0 && (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No new notifications
        </Typography>
      </Box>
    )}
    {notifications.map((notification) => (
      <MenuItem
        key={notification.id}
        onClick={onClose}
        sx={{
          py: 2,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': { borderBottom: 'none' },
          backgroundColor: notification.read ? 'transparent' : 'action.hover',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {notification.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {notification.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {notification.time}
              </Typography>
            </Box>
            {!notification.read && (
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 1 }}
              />
            )}
          </Box>
        </Box>
      </MenuItem>
    ))}
  </Menu>
);

// ---------------------------------------------------------------------------
// TrendingMenu
// ---------------------------------------------------------------------------

interface TrendingMenuProps {
  anchorEl: HTMLElement | null;
  candidates: TrendingCandidate[];
  onClose: () => void;
}

export const TrendingMenu: React.FC<TrendingMenuProps> = ({ anchorEl, candidates, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{ sx: { mt: 2, width: 320, maxHeight: 480, overflow: 'auto' } }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('trending.title')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Real-time candidate performance
        </Typography>
      </Box>
      {candidates.map((candidate) => (
        <MenuItem
          key={candidate.id}
          onClick={() => {
            navigate(`/candidate/${candidate.id}`);
            onClose();
          }}
          sx={{
            py: 1.5,
            px: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={candidate.profileImage}
                sx={{ bgcolor: candidate.color, width: 40, height: 40, fontSize: '0.875rem', fontWeight: 600 }}
              >
                {candidate.avatar}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {candidate.name}
                  </Typography>
                  {candidate.trend === 'flat' ? (
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
                      {candidate.change}
                    </Typography>
                  ) : candidate.trend === 'up' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                      <TrendingIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.main' }}>
                        {candidate.change}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'error.main' }}>
                      <TrendingIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.main' }}>
                        {candidate.change}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {candidate.party}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidate.mentions}
                </Typography>
              </Box>
            </Box>
          </Box>
        </MenuItem>
      ))}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            navigate('/candidates');
            onClose();
          }}
          sx={{ textTransform: 'none' }}
        >
          {t('trending.viewAll')}
        </Button>
      </Box>
    </Menu>
  );
};

// ---------------------------------------------------------------------------
// ActionsMenu
// ---------------------------------------------------------------------------

interface ActionsMenuProps {
  anchorEl: HTMLElement | null;
  actionsButtonRef: React.RefObject<HTMLButtonElement>;
  unreadCount: number;
  trendingCount: number;
  onClose: () => void;
  onOpenNotifications: (el: HTMLElement) => void;
  onOpenTrending: (el: HTMLElement) => void;
}

export const ActionsMenu: React.FC<ActionsMenuProps> = ({
  anchorEl,
  actionsButtonRef,
  unreadCount,
  trendingCount,
  onClose,
  onOpenNotifications,
  onOpenTrending,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const openDelayed = (opener: (el: HTMLElement) => void) => {
    onClose();
    setTimeout(() => {
      if (actionsButtonRef.current) opener(actionsButtonRef.current);
    }, 100);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{ sx: { mt: 2, minWidth: 320 } }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={() => openDelayed(onOpenNotifications)}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon fontSize="small" />
          </Badge>
        </ListItemIcon>
        <ListItemText>{t('notifications.title')}</ListItemText>
      </MenuItem>

      <MenuItem onClick={() => openDelayed(onOpenTrending)}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Badge badgeContent={trendingCount} color="primary">
            <TrendingIcon fontSize="small" />
          </Badge>
        </ListItemIcon>
        <ListItemText>{t('trending.title')}</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem
        onClick={() => {
          const next = i18n.language === 'en' ? 'es' : 'en';
          i18n.changeLanguage(next);
          onClose();
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <LanguageIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {t('header.language')}:{' '}
          {i18n.language === 'en' ? 'English → Español' : 'Español → English'}
        </ListItemText>
      </MenuItem>

      {location.pathname.startsWith('/candidate/') && [
        <Divider key="divider" />,
        <MenuItem
          key="share"
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: 'Candidate Profile',
                text: 'Check out this candidate profile',
                url: window.location.href,
              });
            }
            onClose();
          }}
        >
          <ListItemIcon>
            <ShareIcon />
          </ListItemIcon>
          <ListItemText>Share Profile</ListItemText>
        </MenuItem>,
        <MenuItem
          key="bookmark"
          onClick={() => {
            onClose();
          }}
        >
          <ListItemIcon>
            <BookmarkIcon />
          </ListItemIcon>
          <ListItemText>Bookmark</ListItemText>
        </MenuItem>,
      ]}
    </Menu>
  );
};
